import { useEffect, useState } from "react";
import { LoadingDiv, SweetAlertShow } from "../master/Master";
import {
  studentFeeTransactionDeleteV4,
  studentFeeTransactions,
} from "../../api/Student";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import Master from "./Master";
import { BankList } from "../../api/BankService";
import { Link } from "react-router-dom";
import { formatToDate } from "../master/FormatToDateTime";
import usePrivateLink from "../config/usePrivateLink";
import Swal from "sweetalert2";
import DownloadExcel from "../master/DownloadExcel";
import { PrintPage } from "../master/Print";
import { batchList } from "../../api/Batch";

export default function FeesTransactions() {
  const hasAccess = usePrivateLink();
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [banks, setBanks] = useState([]);
  const [batches, setBatches] = useState([]);
  const [filters, setFilters] = useState({});
  const [data, setData] = useState({});

  useEffect(() => {
    const parseSessions = JSON.parse(localStorage.getItem("sessions") ?? "[]");
    setSessions(parseSessions);
  }, []);

  useEffect(() => {
    const activeSession = sessions?.find((s) => Number(s.status) === 1)?.id;
    setFilters((prev) => {
      if (prev.session_id || !activeSession) return prev;
      return { ...prev, session_id: activeSession };
    });
  }, [sessions]);

  const getBusinessBanks = async () => {
    setLoading(true);
    try {
      const response = await BankList();
      if (response?.data?.status) {
        setBanks(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getStudentFeeTransactions = async () => {
    setLoading(true);
    try {
      let addUrl = "";
      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (value) addUrl += `&${key}=${encodeURIComponent(value)}`;
      });
      const response = await studentFeeTransactions(addUrl);
      if (response.data.status) {
        setData(response?.data?.data || {});
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.response?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getBatchList = async () => {
    setLoading(true);
    let addUrl = "";
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value) {
        addUrl += `&${key}=${encodeURIComponent(value)}`;
      }
    });
    try {
      const response = await batchList(addUrl);
      if (response.data.status) {
        setBatches(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This Transaction will be permanently deleted. You cannot undo this action.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    setLoading(true);
    try {
      const response = await studentFeeTransactionDeleteV4(id);
      if (response.data.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
      getStudentFeeTransactions();
    }
  };

  useEffect(() => {
    getBusinessBanks();
  }, []);

  useEffect(() => {
    getStudentFeeTransactions();
    getBatchList();
  }, [filters]);

  const inr = (n) =>
    "₹ " +
    Number(n || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    });

  const MODULE_BADGE_COLOR = {
    "Hostel Fee": "success",
    "Transport Fee": "warning",
    "Library Fee": "info",
  };

  const parseModule = (moduleStr) => {
    try {
      const parsed = JSON.parse(moduleStr);
      return parsed.map((m, i) => {
        const color = MODULE_BADGE_COLOR[m.type] || "primary";
        return (
          <span
            key={i}
            className={`badge bg-${color}-subtle text-${color} rounded-pill me-1 mb-1 fw-semibold text-nowrap`}
            style={{ fontSize: "0.7rem" }}
          >
            {m.type} · {inr(m.amount)}
          </span>
        );
      });
    } catch (e) {
      return <span className="text-danger small">Invalid data</span>;
    }
  };

  const paymentBadgeClass = (method) => {
    switch ((method || "").toLowerCase()) {
      case "cash":
        return "bg-success";
      case "online":
      case "upi":
        return "bg-info";
      case "cheque":
        return "bg-warning text-dark";
      case "card":
        return "bg-primary";
      default:
        return "bg-secondary";
    }
  };

  const parseModuleText = (moduleStr) => {
    try {
      const parsed = JSON.parse(moduleStr);

      return parsed.map((m) => `${m.type} (${inr(m.amount)})`).join(", ");
    } catch (e) {
      return "Invalid data";
    }
  };
  const handleDownload = async (type = "excel") => {
    setLoading(true);
    try {
      const downloadData = [
        ...(data?.data?.map((item, index) => ({
          "Student Name":
            item?.student?.name ??
            item?.library_student?.student_name ??
            item?.hostel_student?.student_name ??
            "-",

          "Registration No": item?.student?.registration_no ?? "-",

          "Receipt No": item.receipt_no,

          Module: parseModuleText(item.module),

          Amount: Number(item.paid ?? item.amount ?? 0),

          "Payment Method": item.payment_method,

          "Bank Account": item.bank?.account_no ?? "-",

          "Payment Date": formatToDate(item.payment_date),

          "Created By": item.created_by?.name ?? "-",

          Remark: item.remark ?? "-",
        })) || []),

        {
          "Student Name": "",
          "Registration No": "",
          "Receipt No": "",
          Module: "TOTAL",
          Amount: data?.data?.reduce(
            (total, row) => total + Number(row.paid ?? row.amount ?? 0),
            0,
          ),
          "Payment Method": "",
          "Bank Account": "",
          "Payment Date": "",
          "Created By": "",
          Remark: "",
        },
      ];

      const title =
        batches?.find((f) => Number(f.id) === Number(filters?.batch_id))
          ?.title ?? "Transactions Report";

      if (downloadData.length) {
        if (type === "excel") {
          const now = new Date();

          const fileName = `Report_${now.getFullYear()}_${now.getMonth() + 1
            }_${now.getDate()}_${now.getHours()}_${now.getMinutes()}`;

          DownloadExcel(downloadData, [], fileName, []);
        } else {
          PrintPage(downloadData, title);
        }
      } else {
        SweetAlertShow("info", "Info", "No records found");
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />
                  <div className="col-12">
                    {/* Header / Filters */}
                    <div className="card border-0 shadow-sm rounded-4 p-3 mb-3">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="bg-primary text-white rounded-3 d-flex align-items-center justify-content-center fs-5"
                            style={{ width: 46, height: 46 }}
                          >
                            <i className="ri-money-rupee-circle-line"></i>
                          </div>
                          <div>
                            <h5 className="m-0 fw-bold">Fees Transactions</h5>
                          </div>
                        </div>

                        <div className="row align-items-center g-2 flex-wrap py-2">
                          <div className="col-md-2">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select rounded-3"
                                value={filters?.session_id || ""}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    session_id: e.target.value,
                                  })
                                }
                              >
                                {sessions?.map((s) => (
                                  <option key={s.id} value={s.id}>
                                    {s.session} :{" "}
                                    {s.status === "1" ? "Active" : "Inactive"}
                                  </option>
                                ))}
                              </select>
                              <label>Session</label>
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="form-floating form-floating-outline">
                              <select
                                name="batch_id"
                                id="batch_id"
                                className="form-select"
                                value={filters?.batch_id || ""}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    batch_id: e.target.value,
                                  })
                                }
                              >
                                <option value="">All</option>
                                {batches?.map((item) => (
                                  <option key={item?.id} value={item?.id}>
                                    {item?.title} ({item?.course?.title})
                                  </option>
                                ))}
                              </select>
                              <label>Batch</label>
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="form-floating form-floating-outline">
                              <select
                                name="bank_id"
                                id="bank_id"
                                className="form-select"
                                value={filters?.bank_id}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    bank_id: e.target.value,
                                  })
                                }
                              >
                                <option value="">All</option>
                                {banks?.map((item) => (
                                  <option key={item?.id} value={item?.id}>
                                    {item?.account_no}:({item?.owner_name})
                                  </option>
                                ))}
                              </select>
                              <label htmlFor="">Select Account</label>
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select rounded-3"
                                value={filters?.rte_status || ""}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    rte_status: e.target.value,
                                  })
                                }
                              >
                                <option value="">All</option>
                                <option value="RTE">RTE</option>
                                <option value="NON-RTE">NON-RTE</option>
                              </select>
                              <label>RTE Status</label>
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="date"
                                name="form_date"
                                id="form_date"
                                value={filters?.form_date || ""}
                                className="form-control"
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    form_date: e.target.value,
                                  })
                                }
                              />
                              <label htmlFor="form_date">Form Date</label>
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="date"
                                name="to_date"
                                id="to_date"
                                value={filters?.to_date || ""}
                                className="form-control"
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    to_date: e.target.value,
                                  })
                                }
                              />
                              <label htmlFor="to_date">To Date</label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ============ Summary Cards ============ */}
                    <div className="row g-3 mb-3">
                      {[
                        {
                          label: "Students",
                          value: data?.students || 0,
                          icon: "ri-group-line",
                          accent: "#4f46e5",
                          isCurrency: false,
                          isFeeType: false,
                          isActive: false,
                        },
                        {
                          label: "Transactions",
                          value: data?.transactions || 0,
                          icon: "ri-exchange-line",
                          accent: "#0ea5e9",
                          isCurrency: false,
                          isFeeType: false,
                          isActive: false,
                        },
                        {
                          label: "Batch Fee",
                          value: data?.total_batch_fee_paid || 0,
                          icon: "ri-graduation-cap-line",
                          accent: "#6366f1",
                          isCurrency: true,
                          isFeeType: "batch_fee",
                          isActive: filters?.fee_type === "batch_fee",
                        },
                        {
                          label: "Hostel Fee",
                          value: data?.total_hostel_fee_paid || 0,
                          icon: "ri-hotel-bed-line",
                          accent: "#22c55e",
                          isCurrency: true,
                          isFeeType: "hostel_fee",
                          isActive: filters?.fee_type === "hostel_fee",
                        },
                        {
                          label: "Transport Fee",
                          value: data?.total_transport_fee_paid || 0,
                          icon: "ri-bus-line",
                          accent: "#f59e0b",
                          isCurrency: true,
                          isFeeType: "transport_fee",
                          isActive: filters?.fee_type === "transport_fee",
                        },
                        {
                          label: "Library Fee",
                          value: data?.total_library_fee_paid || 0,
                          icon: "ri-book-2-line",
                          accent: "#06b6d4",
                          isCurrency: true,
                          isFeeType: "library_fee",
                          isActive: filters?.fee_type === "library_fee",
                        },
                        {
                          label: "Total Paid",
                          value: data?.total_paid || 0,
                          icon: "ri-wallet-3-line",
                          accent: "#10b981",
                          isCurrency: true,
                          isFeeType: false,
                          isActive: false,
                        },
                      ]?.map((k) => (
                        <div
                          className={`col ${k?.isFeeType ? "cursor-pointer" : ""}`}
                          key={k.label}
                          onClick={() => {
                            setFilters({ ...filters, fee_type: k?.isFeeType });
                          }}
                        >
                          <div
                            className={`card shadow-sm rounded-4 h-100 ${k?.isActive ? "border border-2" : "border-0"
                              }`}
                            style={{
                              borderColor: `${k?.isActive ? k.accent : undefined} !important`,
                              borderTop: `3px solid ${k.accent} !important`,
                            }}
                          >
                            <div className="card-body p-3">
                              <div
                                className="rounded-3 d-flex align-items-center justify-content-center mb-2 fs-6"
                                style={{
                                  width: 42,
                                  height: 42,
                                  background: `${k.accent}1a`,
                                  color: k.accent,
                                }}
                              >
                                <i className={k.icon}></i>
                              </div>
                              <h5
                                className="m-0 fw-bold"
                                style={{ color: k.accent }}
                              >
                                {k.isCurrency ? inr(k.value) : k.value}
                              </h5>
                              <small className="text-muted text-uppercase fw-semibold">
                                {k.label}
                              </small>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* ============ Transactions Table ============ */}
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-3">
                      <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center text-white"
                            style={{
                              width: 44,
                              height: 44,
                              background:
                                "linear-gradient(135deg, #10b981, #22c55e)",
                            }}
                          >
                            <i className="ri-file-list-3-line fs-5"></i>
                          </div>
                          <div>
                            <h6 className="m-0 fw-bold">Transaction List</h6>
                            <small className="text-muted">
                              All fee receipts matching the selected filters
                            </small>
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <span className="badge bg-light text-dark border fw-semibold px-3 py-2 rounded-pill">
                            <i className="ri-list-check me-1"></i>
                            {data?.data?.length || 0} record
                            {data?.data?.length === 1 ? "" : "s"}
                          </span>
                          {hasAccess("/fees/excel/list") && (
                            <button
                              type="button"
                              className="btn btn-outline-primary rounded-3"
                              onClick={() => handleDownload("excel")}
                            >
                              <i className="ri-file-excel-2-line me-2"></i>{" "}
                              Download
                            </button>
                          )}
                          {hasAccess("/fees/print/list") && (
                            <button
                              type="button"
                              className="btn btn-outline-info rounded-3"
                              onClick={() => handleDownload("print")}
                            >
                              <i className="ri-printer-line me-2"></i> Print
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead>
                            <tr className="text-uppercase small text-muted">
                              <th className="ps-4 py-3 fw-semibold">#</th>
                              <th className="py-3 fw-semibold">Student</th>
                              <th className="py-3 fw-semibold">Receipt</th>
                              <th className="py-3 fw-semibold">Module</th>
                              <th className="py-3 fw-semibold text-end">
                                Amount
                              </th>
                              <th className="py-3 fw-semibold">Payment</th>
                              <th className="py-3 fw-semibold">Bank</th>
                              <th className="py-3 fw-semibold">Date</th>
                              <th className="py-3 fw-semibold">Created By</th>
                              <th className="py-3 fw-semibold">Remark</th>
                              <th className="py-3 fw-semibold pe-4 text-end">
                                Action
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {data?.data?.length > 0 ? (
                              data?.data?.map((item, index) => (
                                <tr key={item.id}>
                                  <td className="ps-4">{index + 1}</td>
                                  <td>
                                    {item?.student_id ? (
                                      hasAccess("/student/details") ? (
                                        <Link
                                          className="fw-semibold text-decoration-none"
                                          to={`/student/details?id=${item?.student_id}`}
                                        >
                                          {item?.student?.name}{" "}
                                          <span className="text-muted fw-normal">
                                            ({item?.student?.registration_no})
                                          </span>
                                        </Link>
                                      ) : (
                                        <span className="fw-semibold">
                                          {item?.student?.name}{" "}
                                          <span className="text-muted fw-normal">
                                            ({item?.student?.registration_no})
                                          </span>
                                        </span>
                                      )
                                    ) : (
                                      <span className="fw-semibold">
                                        {item?.student?.name ??
                                          item?.library_student?.student_name ??
                                          item?.hostel_student?.student_name ??
                                          "-"}
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    <span className="badge bg-dark-subtle text-secondary-emphasis rounded-pill fw-semibold">
                                      {item.receipt_no}
                                    </span>
                                  </td>
                                  <td>{parseModule(item.module)}</td>
                                  <td className="fw-bold text-success text-end  text-nowrap">
                                    {inr(item.paid ?? item.amount)}
                                  </td>
                                  <td>
                                    <span
                                      className={`badge rounded-pill ${paymentBadgeClass(
                                        item.payment_method,
                                      )}`}
                                    >
                                      {item.payment_method}
                                    </span>
                                  </td>
                                  <td>
                                    {item.bank ? item?.bank?.account_no : "-"}
                                  </td>
                                  <td>
                                    <span className="text-nowrap">
                                      {formatToDate(item.payment_date)}
                                    </span>
                                  </td>
                                  <td>{item.created_by?.name || "-"}</td>
                                  <td>
                                    <span
                                      className="text-truncate d-inline-block"
                                      style={{ maxWidth: 140 }}
                                    >
                                      {item.remark || "-"}
                                    </span>
                                  </td>
                                  <td className="pe-4">
                                    <div className="d-flex gap-2 justify-content-end">
                                      {hasAccess("/student/fees/receipt") && (
                                        <Link
                                          className="btn btn-label-primary btn-sm"
                                          to={`/student/fees/receipt?id=${item?.id}`}
                                          title="Print receipt"
                                        >
                                          <i className="ri-printer-line ri-20px"></i>
                                        </Link>
                                      )}
                                      {hasAccess(
                                        "/student/fee/transaction/delete",
                                      ) && (
                                          <button
                                            className="btn btn-label-danger btn-sm"
                                            onClick={() => handleDelete(item?.id)}
                                            title="Delete transaction"
                                          >
                                            <i className="ri-delete-bin-line ri-20px"></i>
                                          </button>
                                        )}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan="11"
                                  className="text-center text-muted py-5"
                                >
                                  <i className="ri-inbox-line fs-2 d-block mb-2 text-secondary"></i>
                                  No Transactions Found
                                </td>
                              </tr>
                            )}
                          </tbody>

                          {data?.data?.length > 0 && (
                            <tfoot>
                              <tr style={{ background: "#f8f9fc" }}>
                                <td
                                  colSpan="4"
                                  className="ps-4 py-3 text-end fw-bold"
                                >
                                  Page Total
                                </td>
                                <td className="py-3 fw-bold text-success text-end text-nowrap">
                                  {inr(
                                    data?.data?.reduce(
                                      (t, r) =>
                                        t + Number(r.paid ?? r.amount ?? 0),
                                      0,
                                    ),
                                  )}
                                </td>
                                <td colSpan="6" className="pe-4"></td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
